/** Request page: the story, explorer, auditor view and ask why (ADR-0057). */
export default {
  en: {
    pageTitle: "What happened",
    pick: { title: "Choose a request to see what happened", body: "Every request to an agent is listed in Activity. Open one to see its story.", action: "Go to Activity" },
    links: { howMade: "How this answer was made", seeWhatHappened: "See what happened" },
    back: "Back",
    title: "{{person}} asked {{agent}}",
    titleQuestion: "{{person}} asked {{agent}}: “{{question}}”",
    loadError: "This request could not be loaded.",
    notFound: "There is no record of this request, or you are not allowed to see it.",
    status: {
      answered: "Answered",
      rejected: "Rejected by an approver",
      failed: "Failed",
      refused: "Refused",
      cancelled: "Cancelled",
      waiting: "Waiting for a person",
      running: "In progress",
    },
    meta: {
      started: "Started {{when}}",
      took: "took {{duration}}",
      sofar: "running for {{duration}}",
      people_one: "1 person",
      people_other: "{{count}} people",
      models: "AI model: {{models}}",
      records_one: "1 signed audit record",
      records_other: "{{count}} signed audit records",
      label: "About this request",
    },
    tabs: { story: "The story", explorer: "Explorer", auditor: "Auditor view", data: "Step by step, with data" },
    data: {
      help: "What passed through each step of this request. Shown for requests in the Development and Testing stages only, to builders: personal data is masked, long values are shortened, and it is kept for {{hours}} hours. Your viewing is recorded.",
      sent: "What it was sent",
      made: "What it made",
      none: "No step data was kept for this request.",
    },
    strip: {
      label: "Steps of this request",
      asked: "Asked",
      policy: "Allowed by the rules",
      policyRefused: "Refused by the rules",
      waited: "Waited for a person",
      approved: "Approved by {{name}}",
      rejected: "Rejected by {{name}}",
      safety: "Guardrail check",
      safetyRefused: "Blocked by the guardrails",
      answered: "Answered",
      answerPending: "Answer",
      refused: "Refused",
      failed: "Failed",
      cancelled: "Stopped",
      question: "Asked a person",
      blocked: "Blocked by safety checks",
      rejected_end: "Rejected",
      replaced: "Replaced by a correction",
      implied: "The platform checks the rules before every run starts.",
    },
    story: {
      label: "What happened, step by step",
      correlated: "Linked by time and person",
      correlatedHelp: "This record was written without the request number; it is matched to this request by who did it and when.",
      sincePrevious: "{{duration}} later",
      highlight: "Highlighted step",
      empty: "No steps were recorded.",
      showAll: "Show all {{count}} steps",
      showLess: "Show fewer steps",
      where: { run: "Request", case: "Case", task: "Task", audit: "Audit record", node: "Agent step" },
    },
    claim: {
      case: "a case or ticket was opened",
      notify: "a message or notification was sent",
      payment: "a payment was made",
      access: "access was granted",
      booking: "something was booked",
      and: " and ",
    },
    step: {
      asked: { default: "{{actor}} asked {{agent}} for help" },
      approval_requested: {
        default: "{{agent}} paused and asked someone with the role {{roles}} to approve",
        waiting: "{{agent}} is waiting for someone with the role {{roles}} to approve",
        unanswered: "{{agent}} asked someone with the role {{roles}} to approve; nobody decided before the request ended",
      },
      question_asked: {
        choose_waiting: "{{agent}} is waiting for someone with the role {{roles}} to choose",
        choose_answered: "{{agent}} asked someone with the role {{roles}} to choose, and they did",
        choose_unanswered: "{{agent}} asked someone with the role {{roles}} to choose; nobody answered before the request ended",
        input_waiting: "{{agent}} is waiting for someone to give the information it asked for",
        input_answered: "{{agent}} asked a person for information, and they answered",
        input_unanswered: "{{agent}} asked a person for information; nobody answered before the request ended",
        default: "{{agent}} asked a person",
      },
      approval_skipped: { default: "Safe test: the approval by someone with the role {{roles}} was skipped — nobody was asked" },
      approval_reminded: { default: "Nobody had decided half-way through the time: a reminder went to {{roles}}" },
      approval_escalated: { default: "Nobody decided in time: the request went on to someone with the role {{roles}}" },
      correction: { default: "This request replaced an earlier one after the person corrected it" },
      claim_unbacked: { default: "The answer said {{claims}}, but no system did it in this request. The person was told plainly." },
      limit_reached: {
        rerouted: "The step {{step}} reached its repeat limit, so {{agent}} went on at {{target}}",
        stopped: "The step {{step}} reached its repeat limit, so this path stopped",
        default: "The step {{step}} reached its repeat limit",
      },
      approval_decided: {
        approved: "{{actor}} approved",
        rejected: "{{actor}} rejected the request",
        edited: "{{actor}} approved with changes",
        refused: "{{actor}} tried to approve, but it was refused",
        default: "{{actor}} recorded a decision",
      },
      policy: {
        allowed: "The rules allowed {{action}}",
        refused: "The rules refused {{action}}",
        default: "The rules checked {{action}}",
      },
      safety_check: {
        passed: "The guardrails passed the {{direction}}",
        blocked: "The guardrails blocked the {{direction}}",
        redacted: "The guardrails hid sensitive data in the {{direction}}",
        unchecked: "The safety check of the {{direction}} could not run",
        default: "The guardrails looked at the {{direction}}",
      },
      model_call: {
        done: "{{agent}} asked the AI model {{model}}",
        refused: "{{agent}} was not allowed to use the AI model {{model}}",
        default: "{{agent}}'s call to the AI model {{model}} failed",
      },
      tool_call: {
        done: "{{agent}} used {{tool}}",
        refused: "{{agent}} tried to use {{tool}}, but the rules refused it",
        failed: "{{agent}} tried to use {{tool}}, but it failed",
        simulated: "{{agent}} would have used {{tool}} — safe test, only simulated",
        default: "{{agent}} tried to use {{tool}}, but it failed",
      },
      tool_compensation: { done: "{{agent}} undid {{tool}}", default: "{{agent}} could not undo {{tool}}" },
      retrieval: { default: "{{agent}} searched {{collection}} and found {{count}} sources", named: "{{agent}} searched {{collection}}: {{documents}}" },
      handover: { default: "The conversation was handed to a person on the desk" },
      handover_resumed: { default: "{{actor}} handed the conversation back to {{agent}}" },
      memory_read: { default: "{{agent}} read its memory" },
      memory_write: {
        default: "{{agent}} saved to its memory",
        done: "{{agent}} kept what the person said for next time",
        failed: "{{agent}} could not keep what the person said for next time",
        skipped: "{{agent}} kept nothing new for next time",
      },
      conversation_context: { default: "{{agent}} read the conversation so far", documents: "{{agent}} read the conversation so far and the attached documents" },
      prompt: { default: "{{agent}} used the instructions {{prompt}}" },
      decision: {
        decided: "Decided: {{value}} — {{pct}}% sure",
        decidedNoPct: "Decided: {{value}}",
        chosen: "Not sure ({{pct}}%) — asked {{actor}}, who chose {{value}}",
        chosenNoPct: "Not sure — asked {{actor}}, who chose {{value}}",
        asked_person: "Only {{pct}}% sure it is {{value}} (needs {{bar}}%), so it asked a person to choose",
        default: "Decided: {{value}}",
      },
      finished: {
        default: "{{agent}} answered",
        blocked: "The request ended: the safety checks blocked it",
        refused: "The request ended: the rules refused part of it",
        rejected: "The request ended: an approver rejected it",
      },
      failed: { default: "The request failed" },
      cancelled: { default: "The request was stopped", replaced: "The request was stopped and replaced by a corrected one" },
      unknown: "Something else was recorded ({{kind}})",
    },
    direction: { input: "question", output: "answer", check: "content" },
    category: {
      prompt_injection: "an attempt to override the agent's instructions",
      jailbreak: "an attempt to get around the rules",
      pii: "personal data",
      secrets: "a password or secret",
      toxicity: "harmful language",
      topic: "a topic this agent may not discuss",
      grounding: "an answer not supported by its sources",
    },
    content: {
      title: "Stored conversation content",
      help: "Your organisation keeps the content of requests as evidence. Showing it is recorded in the audit trail.",
      show: "Show the content (this is recorded)",
      none: "No content was kept for this request (the setting was off, or its time has passed).",
      download: "Download as evidence (JSON)",
      conversation: "Sent to and answered by the AI",
      kind: { input: "Sent to the step ({{key}})", output: "Written by the step ({{key}})" },
      until: "until {{date}}",
      col: { step: "Step", what: "What", value: "Content (personal data masked)", kept: "Kept" },
    },
    dataLevel: { public: "Public", internal: "Internal", confidential: "Confidential", restricted: "Restricted" },
    why: {
      label: "Why",
      local: "Answered by a model on our own servers.",
      online: "Answered by an online model.",
      localBecause: "Answered by a model on our own servers because the request was {{level}}; online models are not allowed for that data level.",
      fallback: "The first choice was unavailable, so a backup answered (instead of {{from}}).",
      fallbackUnknown: "the first choice",
      channel: "It came in through {{channel}}.",
      channelName: {
        web: "the console",
        api: "the API (a program using an API key)",
        mcp: "an AI app connected over MCP",
        widget: "the website chat",
        email: "e-mail",
        chat: "a chat app",
        voice: "voice",
      },
      four_eyes: "The person who asked cannot approve their own request (four-eyes rule).",
      decision_role_missing: "Only people with the right role may decide this kind of task.",
      human_only: "Only a person, acting directly, may do this.",
      clearance: "The data is classified above this person's clearance.",
      kill_switch: "An emergency stop was switched on.",
      approval_node: "This agent always asks a person before it answers.",
      policy_approval: "The action's risk or amount needs a person's approval.",
      org_rule_refused: "Your organisation's rule \u201c{{rule}}\u201d does not allow this.",
      org_rule_approval: "Your organisation's rule \u201c{{rule}}\u201d asks for a person's approval.",
      policy_denied: "The rules do not allow this.",
      role_missing: "The person does not have the role this action needs.",
      budget_exceeded: "The spending limit was reached.",
      timeout: "It took too long and was stopped.",
      guardrail_blocked: "The guardrails found something it does not allow.",
      cancelled_by_user: "The person who asked cancelled it.",
      cancelled: "It was cancelled.",
      other: "Recorded reason: {{raw}}",
      waited: "Waited {{duration}} for a decision.",
      signed: "Signed with the approver's own key.",
      model: "{{input}} tokens in, {{output}} tokens out · {{latency}}",
      attempts: "Tried {{count}} times in all, waiting {{waits}} between tries.",
      simulated: "Safe test: nothing was really changed in the system.",
      unchecked: "The check could not run, so this was not checked.",
      superseded: "The person corrected the request while it was working.",
      low_confidence: "The AI was not sure enough, so a person decides.",
      policyVersion: "Rules version {{version}}.",
      uncalibrated: "The certainty is the model's own estimate and has not been calibrated.",
      calibrated: "The certainty is calibrated against past decisions.",
      triggered: "Found: {{what}}.",
      ownStoppedQuestion: "Your guardrail “{{name}}” stopped this question.",
      ownStoppedAnswer: "Your guardrail “{{name}}” stopped this answer.",
      ownHid: "Your guardrail “{{name}}” hid part of the text.",
      ownNoted: "Your guardrail “{{name}}” found something; it was only noted.",
    },
    actor: {
      you: "You",
      person: "Person {{n}}",
      agent: "the agent",
      platform: {
        policy: "The rules",
        guardrails: "Guardrail check",
        cases: "Case service",
        orchestrator: "The platform",
      },
      model: "AI model {{id}}",
      tool: "{{id}}",
    },
    roles: {
      requester: "Asked",
      approver: "Approver",
      decider: "Chose for the agent",
      agent: "Agent",
      policy: "Rules",
      safety: "Safety",
      model: "AI model",
      trigger: "Started it",
    },
    side: {
      who: "Who was involved",
      where: "Where to find it",
      openCase: "Open the case",
      openTask: "Open the task",
      openAudit: "See the signed records",
      technical: "Technical details",
      runId: "Request number",
      agentId: "Agent",
      specDigest: "Agent version digest",
      policyVersions: "Rules version",
      firstHash: "First record hash",
      lastHash: "Last record hash",
      seqRange: "Record numbers",
      traceIds: "Trace ids",
      none: "—",
      noAuditAccess: "Only auditors can see the signed records.",
    },
    explorer: {
      label: "Explorer",
      views: { graph: "Graph", tree: "Tree", person: "By person" },
      search: "Filter",
      searchPlaceholder: "Filter by name, rule, model…",
      request: "This request",
      groups: { people: "People", agent: "Agent", rules: "Rules and safety", model: "AI model", records: "Records" },
      expand: "Show {{name}}",
      collapse: "Hide {{name}}",
      more: "{{count}} more",
      noMatch: "Nothing matches the filter.",
      graphHelp: "Select an item to see how it connects to the request. Use Tab to move between items and Enter to open a group.",
      details: "Details",
      detailsEmpty: "Select something in the explorer to see what it means.",
      facts: "Key facts",
      fact: { provider: "Provider", version: "Version", alias: "Model name used by the agent" },
      goTo: "Go to",
      showInStory: "Show in the story",
      steps_one: "1 step",
      steps_other: "{{count}} steps",
      child: {
        person: "{{name}}",
        agent: "{{name}}",
        node: "Step: {{name}}",
        tool: "Tool: {{name}}",
        policy: "Rule check: {{name}}",
        safety: "Guardrail check of the {{name}}",
        rule: "{{name}}",
        model: "{{name}}",
        case: "Case",
        task: "Task",
        audit: "Audit trail ({{count}} records)",
      },
      rule: {
        approval_required: "A person must approve",
        four_eyes: "Four-eyes rule",
        role_missing: "Role required",
        policy_denied: "Not allowed by the rules",
      },
      explain: {
        request: "Everything that was recorded for this request, grouped by who and what was involved.",
        people: "The people who asked, approved or tried to act on this request.",
        agent: "The agent that handled the request, the steps of its plan it went through and the tools it used.",
        rules: "The rule checks and guardrail checks the platform made along the way.",
        model: "The AI models the agent used, through the platform's model gateway.",
        records: "Where this request is kept: its case, its tasks and its signed audit trail.",
        person: "{{name}} took part in this request as: {{roles}}.",
        agentNode: "The agent {{name}} handled this request.",
        node: "A step in the agent's plan. Visited in this request: {{visited}}.",
        tool: "A connected system the agent used.",
        policy: "The rules engine decided whether this action may happen.",
        safety: "The guardrails looked for harmful content and personal data.",
        rule: "A rule that shaped what happened.",
        modelChild: "An AI model, called through the model gateway: {{calls}} calls.",
        case: "The case that keeps this request with its tasks and documents.",
        task: "A task in a person's inbox, created when the agent asked for approval.",
        audit: "Every step of this request is a record in the tamper-evident audit chain.",
      },
      yes: "yes",
      no: "no",
      plan: "The agent's plan",
      planVisited: "Steps taken",
      planNotVisited: "Not used this time",
      planUnavailable: "The agent's plan is not available for this version.",
    },
    tags: { simulated: "simulated", unchecked: "not checked", asked_person: "asked a person", unanswered: "no answer", rerouted: "went on elsewhere", stopped: "stopped", skipped: "skipped", done: "done", waited: "waited", waiting: "waiting", refused: "refused", allowed: "allowed", failed: "failed", approved: "approved", rejected: "rejected", passed: "passed", blocked: "blocked", decided: "decided", chosen: "chose", answered: "answered", cancelled: "stopped", redacted: "hidden data", edited: "edited", recorded: "recorded" },
    auditor: {
      title: "Who called whom",
      sequence: "Sequence of calls",
      records: "Signed audit records",
      chainOk: "The audit chain verifies: {{checked}} records checked, {{checkpoints}} signed checkpoints valid.",
      chainBad: "The audit chain does NOT verify: {{errors}}",
      chainUnknown: "The audit chain could not be verified right now.",
      links: "{{ok}} consecutive records link to the one before (hash of the previous record).",
      col: { seq: "No.", at: "Time", from: "From", to: "To", type: "Record", hash: "Hash", prev: "Previous hash", step: "Step" },
      correlated: "matched",
      forbidden: "Only auditors, platform auditors and organisation administrators can see the signed records.",
      payload: "Record contents",
    },
    ask: {
      title: "Ask why",
      label: "Your question about this request",
      placeholder: "For example: why did it take so long?",
      submit: "Ask",
      suggestions: "Try",
      s1: "Why did it take so long?",
      s2: "Why was someone refused?",
      s3: "Who approved this?",
      disclaimer: "Answered by AI from this request's records.",
      notGrounded: "This answer does not point to a recorded step. Check the story before relying on it.",
      unknown: "The records don't answer this question.",
      citations: "Based on",
      cite: "Step {{n}}",
      citeLabel: "Show step {{n}} in the story",
      error: "The question could not be answered: {{message}}",
      tooMany: "You have asked a lot of questions. Please wait a minute.",
      blocked: "The guardrails did not allow this question.",
      thinking: "Looking at the records…",
    },
    // Run story (fix wave 2, stream E): why memory was or was not kept.
    memoryWhy: {
      unavailable: "Nothing was kept: the memory service did not answer.",
      nothing_said: "There was nothing worth keeping.",
      nothing_to_keep: "There was nothing worth keeping.",
      already_known: "It already knew this.",
      not_allowed: "The person has not agreed that this agent may remember things about them.",
      blocked: "The guardrails did not allow keeping it.",
      other: "Nothing was kept.",
      attempts: "Tried {{count}} times.",
    },
  },
  ar: {
    pageTitle: "ما الذي حدث",
    pick: { title: "اختر طلبًا لترى ما الذي حدث", body: "كل الطلبات المقدَّمة إلى الوكلاء مدرجة في صفحة النشاط. افتح أحدها لترى قصته.", action: "الذهاب إلى النشاط" },
    links: { howMade: "كيف أُعدّت هذه الإجابة", seeWhatHappened: "اعرف ما الذي حدث" },
    back: "رجوع",
    title: "طلب {{person}} من {{agent}}",
    titleQuestion: "سؤال {{person}} إلى {{agent}}: «{{question}}»",
    loadError: "تعذّر تحميل هذا الطلب.",
    notFound: "لا يوجد سجل لهذا الطلب، أو ليس لديك صلاحية الاطلاع عليه.",
    status: {
      answered: "تمت الإجابة",
      rejected: "رفضه أحد المعتمدين",
      failed: "فشل",
      refused: "مرفوض",
      cancelled: "أُلغي",
      waiting: "بانتظار شخص",
      running: "قيد التنفيذ",
    },
    meta: {
      started: "بدأ {{when}}",
      took: "استغرق {{duration}}",
      sofar: "يعمل منذ {{duration}}",
      people_zero: "لا أشخاص",
      people_one: "شخص واحد",
      people_two: "شخصان",
      people_few: "{{count}} أشخاص",
      people_many: "{{count}} شخصًا",
      people_other: "{{count}} شخص",
      models: "نموذج الذكاء الاصطناعي: {{models}}",
      records_zero: "لا سجلات تدقيق",
      records_one: "سجل تدقيق موقّع واحد",
      records_two: "سجلا تدقيق موقّعان",
      records_few: "{{count}} سجلات تدقيق موقّعة",
      records_many: "{{count}} سجلًا موقّعًا",
      records_other: "{{count}} سجل تدقيق موقّع",
      label: "عن هذا الطلب",
    },
    tabs: { story: "القصة", explorer: "المستكشف", auditor: "عرض المدقق", data: "خطوة بخطوة، مع البيانات" },
    data: {
      help: "ما مرّ عبر كل خطوة في هذا الطلب. يُعرض لطلبات مرحلتي التطوير والاختبار فقط، ولمُنشئي الوكلاء: تُخفى البيانات الشخصية، وتُختصر القيم الطويلة، وتُحفظ {{hours}} ساعة. ويُسجَّل اطلاعك عليها.",
      sent: "ما أُرسل إليها",
      made: "ما أنتجته",
      none: "لم تُحفظ بيانات خطوات لهذا الطلب.",
    },
    strip: {
      label: "مراحل هذا الطلب",
      asked: "طُلب",
      policy: "سمحت به القواعد",
      policyRefused: "رفضته القواعد",
      waited: "انتظر شخصًا",
      approved: "وافق عليه {{name}}",
      rejected: "رفضه {{name}}",
      safety: "فحص الضوابط الوقائية",
      safetyRefused: "حجبته الضوابط الوقائية",
      answered: "تمت الإجابة",
      answerPending: "الإجابة",
      refused: "مرفوض",
      failed: "فشل",
      cancelled: "أُوقف",
      question: "سأل شخصًا",
      blocked: "حجبته فحوص الأمان",
      rejected_end: "رُفض",
      replaced: "استُبدل بتصحيح",
      implied: "تتحقق المنصة من القواعد قبل بدء كل تشغيل.",
    },
    story: {
      label: "ما الذي حدث خطوة بخطوة",
      correlated: "مربوط بالوقت والشخص",
      correlatedHelp: "كُتب هذا السجل دون رقم الطلب، فرُبط بهذا الطلب بحسب من قام به ومتى.",
      sincePrevious: "بعد {{duration}}",
      highlight: "الخطوة المحددة",
      empty: "لم تُسجَّل أي خطوات.",
      showAll: "عرض كل الخطوات ({{count}})",
      showLess: "عرض خطوات أقل",
      where: { run: "الطلب", case: "الحالة", task: "المهمة", audit: "سجل التدقيق", node: "خطوة الوكيل" },
    },
    claim: {
      case: "حالة أو تذكرة فُتحت",
      notify: "رسالة أو إشعارًا أُرسل",
      payment: "مبلغًا دُفع",
      access: "صلاحية مُنحت",
      booking: "شيئًا حُجز",
      and: " و",
    },
    step: {
      asked: { default: "طلب {{actor}} المساعدة من {{agent}}" },
      approval_requested: {
        default: "توقّف {{agent}} وطلب موافقة شخص لديه دور {{roles}}",
        waiting: "ينتظر {{agent}} موافقة شخص لديه دور {{roles}}",
        unanswered: "طلب {{agent}} موافقة شخص لديه دور {{roles}}، ولم يقرر أحد قبل انتهاء الطلب",
      },
      question_asked: {
        choose_waiting: "ينتظر {{agent}} أن يختار شخص لديه دور {{roles}}",
        choose_answered: "طلب {{agent}} من شخص لديه دور {{roles}} أن يختار، فاختار",
        choose_unanswered: "طلب {{agent}} من شخص لديه دور {{roles}} أن يختار، ولم يُجب أحد قبل انتهاء الطلب",
        input_waiting: "ينتظر {{agent}} أن يقدّم شخص المعلومات التي طلبها",
        input_answered: "طلب {{agent}} معلومات من شخص، فأجاب",
        input_unanswered: "طلب {{agent}} معلومات من شخص، ولم يُجب أحد قبل انتهاء الطلب",
        default: "سأل {{agent}} شخصًا",
      },
      approval_skipped: { default: "اختبار آمن: تُخطّيت موافقة صاحب دور {{roles}} — لم يُسأل أحد" },
      approval_reminded: { default: "لم يقرّر أحد حتى منتصف المدة: أُرسل تذكير إلى {{roles}}" },
      approval_escalated: { default: "لم يقرّر أحد في الوقت: انتقل الطلب إلى صاحب دور {{roles}}" },
      correction: { default: "حلّ هذا الطلب محل طلب سابق بعد أن صحّحه صاحبه" },
      claim_unbacked: { default: "قالت الإجابة إن {{claims}}، لكن لم يقم أي نظام بذلك في هذا الطلب. وأُخبر الشخص بذلك بوضوح." },
      limit_reached: {
        rerouted: "بلغت الخطوة {{step}} حدّ التكرار، فتابع {{agent}} من {{target}}",
        stopped: "بلغت الخطوة {{step}} حدّ التكرار، فتوقف هذا المسار",
        default: "بلغت الخطوة {{step}} حدّ التكرار",
      },
      approval_decided: {
        approved: "وافق {{actor}}",
        rejected: "رفض {{actor}} الطلب",
        edited: "وافق {{actor}} مع تعديلات",
        refused: "حاول {{actor}} الموافقة لكن ذلك رُفض",
        default: "سجّل {{actor}} قرارًا",
      },
      policy: {
        allowed: "سمحت القواعد بـ {{action}}",
        refused: "رفضت القواعد {{action}}",
        default: "تحققت القواعد من {{action}}",
      },
      safety_check: {
        passed: "اجتاز {{direction}} الضوابط الوقائية",
        blocked: "حجب الضوابط الوقائية {{direction}}",
        redacted: "أخفى الضوابط الوقائية بيانات حساسة في {{direction}}",
        unchecked: "تعذّر تشغيل فحص الأمان على {{direction}}",
        default: "الضوابط الوقائية {{direction}}",
      },
      model_call: {
        done: "استعان {{agent}} بنموذج الذكاء الاصطناعي {{model}}",
        refused: "لم يُسمح لـ {{agent}} باستخدام النموذج {{model}}",
        default: "فشل طلب {{agent}} من النموذج {{model}}",
      },
      tool_call: {
        done: "استخدم {{agent}} {{tool}}",
        refused: "حاول {{agent}} استخدام {{tool}} لكن القواعد رفضت ذلك",
        failed: "حاول {{agent}} استخدام {{tool}} لكنه فشل",
        simulated: "كان {{agent}} سيستخدم {{tool}} — اختبار آمن، محاكاة فقط",
        default: "حاول {{agent}} استخدام {{tool}} لكنه فشل",
      },
      tool_compensation: { done: "تراجع {{agent}} عن {{tool}}", default: "تعذّر على {{agent}} التراجع عن {{tool}}" },
      retrieval: { default: "بحث {{agent}} في {{collection}} ووجد {{count}} مصادر", named: "بحث {{agent}} في {{collection}}: {{documents}}" },
      handover: { default: "حُوّلت المحادثة إلى موظف في مكتب الموظفين" },
      handover_resumed: { default: "أعاد {{actor}} المحادثة إلى {{agent}}" },
      memory_read: { default: "قرأ {{agent}} من ذاكرته" },
      memory_write: {
        default: "حفظ {{agent}} في ذاكرته",
        done: "احتفظ {{agent}} بما قاله الشخص للمرات القادمة",
        failed: "تعذّر على {{agent}} الاحتفاظ بما قاله الشخص للمرات القادمة",
        skipped: "لم يحتفظ {{agent}} بشيء جديد للمرات القادمة",
      },
      conversation_context: { default: "قرأ {{agent}} المحادثة حتى الآن", documents: "قرأ {{agent}} المحادثة حتى الآن والمستندات المرفقة" },
      prompt: { default: "استخدم {{agent}} التعليمات {{prompt}}" },
      decision: {
        decided: "القرار: {{value}} — بثقة {{pct}}٪",
        decidedNoPct: "القرار: {{value}}",
        chosen: "غير متأكد ({{pct}}٪) — سأل {{actor}} فاختار {{value}}",
        chosenNoPct: "غير متأكد — سأل {{actor}} فاختار {{value}}",
        asked_person: "متأكد بنسبة {{pct}}٪ فقط أنه {{value}} (المطلوب {{bar}}٪)، فطلب من شخص أن يختار",
        default: "القرار: {{value}}",
      },
      finished: {
        default: "أجاب {{agent}}",
        blocked: "انتهى الطلب: حجبته فحوص الأمان",
        refused: "انتهى الطلب: رفضت القواعد جزءًا منه",
        rejected: "انتهى الطلب: رفضه أحد المعتمدين",
      },
      failed: { default: "فشل الطلب" },
      cancelled: { default: "أُوقف الطلب", replaced: "أُوقف الطلب واستُبدل بطلب مصحَّح" },
      unknown: "سُجّل شيء آخر ({{kind}})",
    },
    direction: { input: "السؤال", output: "الإجابة", check: "المحتوى" },
    category: {
      prompt_injection: "محاولة لتجاوز تعليمات الوكيل",
      jailbreak: "محاولة للالتفاف على القواعد",
      pii: "بيانات شخصية",
      secrets: "كلمة مرور أو سر",
      toxicity: "لغة مؤذية",
      topic: "موضوع لا يُسمح لهذا الوكيل بمناقشته",
      grounding: "إجابة لا تدعمها مصادرها",
    },
    content: {
      title: "محتوى المحادثة المحفوظ",
      help: "تحتفظ جهتك بمحتوى الطلبات كدليل. ويُسجَّل عرضه في سجل التدقيق.",
      show: "عرض المحتوى (يُسجَّل ذلك)",
      none: "لم يُحتفظ بأي محتوى لهذا الطلب (كان الإعداد متوقفًا أو انتهت مدته).",
      download: "تنزيل كدليل (JSON)",
      conversation: "ما أُرسل إلى الذكاء الاصطناعي وما أجاب به",
      kind: { input: "ما أُرسل إلى الخطوة ({{key}})", output: "ما كتبته الخطوة ({{key}})" },
      until: "حتى {{date}}",
      col: { step: "الخطوة", what: "ماذا", value: "المحتوى (البيانات الشخصية مخفية)", kept: "المدة" },
    },
    dataLevel: { public: "عام", internal: "داخلي", confidential: "سري", restricted: "مقيّد" },
    why: {
      label: "السبب",
      local: "أجاب نموذج على خوادمنا.",
      online: "أجاب نموذج عبر الإنترنت.",
      localBecause: "أجاب نموذج على خوادمنا لأن الطلب مصنّف {{level}}؛ النماذج عبر الإنترنت غير مسموح بها لهذا المستوى من البيانات.",
      fallback: "لم يكن الخيار الأول متاحًا، فأجاب نموذج احتياطي (بدلًا من {{from}}).",
      fallbackUnknown: "الخيار الأول",
      channel: "وصل الطلب عبر {{channel}}.",
      channelName: {
        web: "وحدة التحكم",
        api: "واجهة البرمجة (برنامج يستخدم مفتاح API)",
        mcp: "تطبيق ذكاء اصطناعي متصل عبر MCP",
        widget: "محادثة الموقع",
        email: "البريد الإلكتروني",
        chat: "تطبيق دردشة",
        voice: "الصوت",
      },
      four_eyes: "لا يمكن لصاحب الطلب الموافقة على طلبه بنفسه (قاعدة الأعين الأربع).",
      decision_role_missing: "لا يقرر في هذا النوع من المهام إلا من لديه الدور المناسب.",
      human_only: "لا يقوم بهذا إلا شخص يتصرف بنفسه مباشرة.",
      clearance: "تصنيف البيانات أعلى من صلاحية هذا الشخص.",
      kill_switch: "تم تشغيل الإيقاف الطارئ.",
      approval_node: "هذا الوكيل يطلب موافقة شخص دائمًا قبل الإجابة.",
      policy_approval: "خطورة الإجراء أو مبلغه تتطلب موافقة شخص.",
      org_rule_refused: "قاعدة جهتك \u201c{{rule}}\u201d لا تسمح بذلك.",
      org_rule_approval: "قاعدة جهتك \u201c{{rule}}\u201d تطلب موافقة شخص.",
      policy_denied: "القواعد لا تسمح بذلك.",
      role_missing: "لا يملك الشخص الدور الذي يتطلبه هذا الإجراء.",
      budget_exceeded: "تم بلوغ حد الإنفاق.",
      timeout: "استغرق وقتًا طويلًا فأُوقف.",
      guardrail_blocked: "وجد الضوابط الوقائية شيئًا غير مسموح به.",
      cancelled_by_user: "ألغاه الشخص الذي طلبه.",
      cancelled: "أُلغي.",
      other: "السبب المسجّل: {{raw}}",
      waited: "انتظر {{duration}} حتى صدر القرار.",
      signed: "موقّع بمفتاح المعتمد الخاص.",
      model: "{{input}} رمزًا مُدخلًا، {{output}} رمزًا مُخرجًا · {{latency}}",
      attempts: "جُرّب {{count}} مرات إجمالًا، بانتظار {{waits}} بين المحاولات.",
      simulated: "اختبار آمن: لم يتغيّر شيء فعليًا في النظام.",
      unchecked: "تعذّر تشغيل الفحص، فلم يُفحص هذا.",
      superseded: "صحّح صاحب الطلب طلبه أثناء العمل.",
      low_confidence: "لم يكن الذكاء الاصطناعي متأكدًا بما يكفي، فيقرر شخص.",
      policyVersion: "إصدار القواعد {{version}}.",
      uncalibrated: "درجة الثقة تقدير النموذج نفسه ولم تُعايَر.",
      calibrated: "درجة الثقة معايَرة على قرارات سابقة.",
      triggered: "وُجد: {{what}}.",
      ownStoppedQuestion: "أوقف ضابطك «{{name}}» هذا السؤال.",
      ownStoppedAnswer: "أوقف ضابطك «{{name}}» هذه الإجابة.",
      ownHid: "أخفى ضابطك «{{name}}» جزءًا من النص.",
      ownNoted: "وجد ضابطك «{{name}}» شيئًا؛ وسُجّل فقط.",
    },
    actor: {
      you: "أنت",
      person: "الشخص {{n}}",
      agent: "الوكيل",
      platform: {
        policy: "القواعد",
        guardrails: "فحص الضوابط الوقائية",
        cases: "خدمة الحالات",
        orchestrator: "المنصة",
      },
      model: "نموذج الذكاء الاصطناعي {{id}}",
      tool: "{{id}}",
    },
    roles: {
      requester: "صاحب الطلب",
      approver: "معتمد",
      decider: "اختار نيابة عن الوكيل",
      agent: "وكيل",
      policy: "القواعد",
      safety: "الأمان",
      model: "نموذج ذكاء اصطناعي",
      trigger: "بدأه",
    },
    side: {
      who: "من شارك",
      where: "أين تجده",
      openCase: "فتح الحالة",
      openTask: "فتح المهمة",
      openAudit: "عرض السجلات الموقّعة",
      technical: "تفاصيل تقنية",
      runId: "رقم الطلب",
      agentId: "الوكيل",
      specDigest: "بصمة إصدار الوكيل",
      policyVersions: "إصدار القواعد",
      firstHash: "بصمة أول سجل",
      lastHash: "بصمة آخر سجل",
      seqRange: "أرقام السجلات",
      traceIds: "معرّفات التتبع",
      none: "—",
      noAuditAccess: "لا يرى السجلات الموقّعة إلا المدققون.",
    },
    explorer: {
      label: "المستكشف",
      views: { graph: "المخطط", tree: "الشجرة", person: "حسب الشخص" },
      search: "تصفية",
      searchPlaceholder: "صفِّ بالاسم أو القاعدة أو النموذج…",
      request: "هذا الطلب",
      groups: { people: "الأشخاص", agent: "الوكيل", rules: "القواعد والأمان", model: "نموذج الذكاء الاصطناعي", records: "السجلات" },
      expand: "إظهار {{name}}",
      collapse: "إخفاء {{name}}",
      more: "{{count}} أخرى",
      noMatch: "لا شيء يطابق التصفية.",
      graphHelp: "اختر عنصرًا لترى كيف يرتبط بالطلب. استخدم Tab للتنقل وEnter لفتح مجموعة.",
      details: "التفاصيل",
      detailsEmpty: "اختر عنصرًا في المستكشف لترى معناه.",
      facts: "حقائق أساسية",
      fact: { provider: "المزوّد", version: "الإصدار", alias: "اسم النموذج لدى الوكيل" },
      goTo: "انتقل إلى",
      showInStory: "عرضه في القصة",
      steps_zero: "لا خطوات",
      steps_one: "خطوة واحدة",
      steps_two: "خطوتان",
      steps_few: "{{count}} خطوات",
      steps_many: "{{count}} خطوة",
      steps_other: "{{count}} خطوة",
      child: {
        person: "{{name}}",
        agent: "{{name}}",
        node: "خطوة: {{name}}",
        tool: "أداة: {{name}}",
        policy: "فحص قاعدة: {{name}}",
        safety: "فحص أمان {{name}}",
        rule: "{{name}}",
        model: "{{name}}",
        case: "الحالة",
        task: "المهمة",
        audit: "سجل التدقيق ({{count}} سجلًا)",
      },
      rule: {
        approval_required: "يجب أن يوافق شخص",
        four_eyes: "قاعدة الأعين الأربع",
        role_missing: "يتطلب دورًا",
        policy_denied: "غير مسموح بحسب القواعد",
      },
      explain: {
        request: "كل ما سُجّل لهذا الطلب، مجمّعًا بحسب من شارك وما استُخدم.",
        people: "الأشخاص الذين طلبوا أو وافقوا أو حاولوا التصرف في هذا الطلب.",
        agent: "الوكيل الذي عالج الطلب، وخطوات خطته التي مرّ بها، والأدوات التي استخدمها.",
        rules: "فحوص القواعد والأمان التي أجرتها المنصة على طول الطريق.",
        model: "نماذج الذكاء الاصطناعي التي استخدمها الوكيل عبر بوابة النماذج في المنصة.",
        records: "أين يُحفظ هذا الطلب: حالته ومهامه وسجل تدقيقه الموقّع.",
        person: "شارك {{name}} في هذا الطلب بصفة: {{roles}}.",
        agentNode: "عالج الوكيل {{name}} هذا الطلب.",
        node: "خطوة في خطة الوكيل. مرّ بها في هذا الطلب: {{visited}}.",
        tool: "نظام متصل استخدمه الوكيل.",
        policy: "قرر محرك القواعد ما إذا كان هذا الإجراء مسموحًا.",
        safety: "بحث الضوابط الوقائية عن محتوى ضار وبيانات شخصية.",
        rule: "قاعدة أثّرت فيما حدث.",
        modelChild: "نموذج ذكاء اصطناعي استُدعي عبر بوابة النماذج: {{calls}} مرات.",
        case: "الحالة التي تحفظ هذا الطلب مع مهامه ومستنداته.",
        task: "مهمة في صندوق شخص، أُنشئت عندما طلب الوكيل الموافقة.",
        audit: "كل خطوة من هذا الطلب سجل في سلسلة تدقيق مقاومة للتلاعب.",
      },
      yes: "نعم",
      no: "لا",
      plan: "خطة الوكيل",
      planVisited: "خطوات نُفّذت",
      planNotVisited: "لم تُستخدم هذه المرة",
      planUnavailable: "خطة الوكيل غير متاحة لهذا الإصدار.",
    },
    tags: { simulated: "محاكاة", unchecked: "لم يُفحص", asked_person: "سأل شخصًا", unanswered: "بلا إجابة", rerouted: "تابع من مكان آخر", stopped: "توقف", skipped: "تُخطّي", done: "تم", waited: "انتظر", waiting: "ينتظر", refused: "رُفض", allowed: "مسموح", failed: "فشل", approved: "وافق", rejected: "رفض", passed: "اجتاز", blocked: "حُجب", decided: "قرّر", chosen: "اختار", answered: "أجاب", cancelled: "أُلغي", redacted: "بيانات مخفية", edited: "عُدّل", recorded: "سُجّل" },
    auditor: {
      title: "من استدعى من",
      sequence: "تسلسل الاستدعاءات",
      records: "سجلات التدقيق الموقّعة",
      chainOk: "سلسلة التدقيق سليمة: فُحص {{checked}} سجلًا، و{{checkpoints}} نقاط تحقق موقّعة صالحة.",
      chainBad: "سلسلة التدقيق غير سليمة: {{errors}}",
      chainUnknown: "تعذّر التحقق من سلسلة التدقيق الآن.",
      links: "{{ok}} سجلات متتالية مرتبطة بالسجل الذي قبلها (بصمة السجل السابق).",
      col: { seq: "رقم", at: "الوقت", from: "من", to: "إلى", type: "السجل", hash: "البصمة", prev: "البصمة السابقة", step: "الخطوة" },
      correlated: "مُطابق",
      forbidden: "لا يرى السجلات الموقّعة إلا المدققون ومدققو المنصة ومسؤولو الجهة.",
      payload: "محتوى السجل",
    },
    ask: {
      title: "اسأل لماذا",
      label: "سؤالك عن هذا الطلب",
      placeholder: "مثلًا: لماذا استغرق وقتًا طويلًا؟",
      submit: "اسأل",
      suggestions: "جرّب",
      s1: "لماذا استغرق وقتًا طويلًا؟",
      s2: "لماذا رُفض أحدهم؟",
      s3: "من وافق على هذا؟",
      disclaimer: "أجاب الذكاء الاصطناعي من سجلات هذا الطلب.",
      notGrounded: "هذه الإجابة لا تشير إلى خطوة مسجّلة. راجع القصة قبل الاعتماد عليها.",
      unknown: "السجلات لا تجيب عن هذا السؤال.",
      citations: "استنادًا إلى",
      cite: "الخطوة {{n}}",
      citeLabel: "عرض الخطوة {{n}} في القصة",
      error: "تعذّرت الإجابة عن السؤال: {{message}}",
      tooMany: "طرحت أسئلة كثيرة. انتظر دقيقة من فضلك.",
      blocked: "لم يسمح الضوابط الوقائية بهذا السؤال.",
      thinking: "جارٍ الاطلاع على السجلات…",
    },
    // Run story (fix wave 2, stream E): why memory was or was not kept.
    memoryWhy: {
      unavailable: "لم يُحفظ شيء: خدمة الذاكرة لم تستجب.",
      nothing_said: "لم يكن هناك ما يستحق الاحتفاظ به.",
      nothing_to_keep: "لم يكن هناك ما يستحق الاحتفاظ به.",
      already_known: "كان يعرف ذلك من قبل.",
      not_allowed: "لم يوافق الشخص على أن يتذكّر هذا الوكيل أشياء عنه.",
      blocked: "لم تسمح الضوابط الوقائية بالاحتفاظ به.",
      other: "لم يُحفظ شيء.",
      attempts: "عدد المحاولات: {{count}}.",
    },
  },
};
